import {AfterViewInit, Component, computed, inject, OnInit, signal, ViewChild,} from "@angular/core";
import {BootstrapIcon, HeaderService} from "../../services/header.service";
import {UserService} from "../../services/user.service";
import {CookieService} from "ngx-cookie-service";
import {KnowledgeBaseService} from "../knowledge-database/data-access/knowledge-base.service";
import {ProductionLine} from "../../shared/interfaces/productionLine";
import {WorkplaceAccessService} from "./data-access/workplace-access.service";
import {Userrole} from "../../shared/interfaces/user";
import {Router} from "@angular/router";
import {SearchFilterBarComponent} from "../../shared/components/search-filter-bar/search-filter-bar.component";
import {
  FilterInterfaceComponent,
  FilterParams,
} from "../../shared/components/filter-interface/filter-interface.component";
import {BehaviorSubject, filter, map, startWith} from "rxjs";
import {ApiService} from "../../services/api.service";
import {WorkplaceCardComponent} from "./components/workplace-card/workplace-card.component";
import {CommonModule, formatDate} from "@angular/common";
import {SearchService} from "../knowledge-database/data-access/search.service";
import {TableType} from "../dashboard/components/report-table/table-config";
import {PieChartComponent, PieChartData} from "../../shared/components/charts/pie-chart/pie-chart.component";
import {StandstillStatistics} from "../workplace-report/workplace-report.component";

@Component({
  selector: "app-workplaces",
  templateUrl: "./workplaces.component.html",
  styleUrls: ["./workplaces.component.scss"],
  standalone: true,
  imports: [
    CommonModule,
    FilterInterfaceComponent,
    WorkplaceCardComponent,
    SearchFilterBarComponent,
    PieChartComponent,
  ],
})
export class WorkplacesComponent implements OnInit {
  router = inject(Router);
  headerService = inject(HeaderService);
  userService = inject(UserService);
  cookieService = inject(CookieService);
  workplaceService = inject(WorkplaceAccessService);
  knowledgeBaseService = inject(KnowledgeBaseService);
  apiService = inject(ApiService);
  searchService = inject(SearchService);

  @ViewChild("searchBar") searchBar!: SearchFilterBarComponent;
  // @ViewChild("reportFilter") reportFilter!: FilterInterfaceComponent;

  displayedColumns: string[] = ["id", "time", "reason"];
  PAGE_SIZE = 5;

  Userrole = Userrole;
  BootstrapIcon = BootstrapIcon;

  showFilterChartSection = signal<boolean>(false);
  showFilterInterface = signal<boolean>(true);
  private filterParams = signal<FilterParams | null>(null);
  protected chartData = signal<PieChartData | null>(null);

  activeReports = computed(() => this.searchService.state().activeReports.data);
  solvedReports = computed(() => this.searchService.state().solvedReports.data);
  escalatedReports = computed(() => this.searchService.state().escalatedReports.data);

  totalPages = computed(() => ({
    activeReports: this.searchService.state().activeReports.totalPages,
    solvedReports: this.searchService.state().solvedReports.totalPages,
    escalatedReports: this.searchService.state().escalatedReports.totalPages,
  }))

  ngOnInit(): void {
    this.headerService.setHeaderTitle("Stillstandshistorie");
    this.headerService.setHeaderSubTitle(
      this.workplaceService.selectedProductionLineSignal()?.name ||
      "Gesamtübersicht"
    );
    this.headerService.setHeaderIcon(BootstrapIcon.workplaces);
    this.headerService.setBackgroundColor("#ffffff");

    if (this.userService.userrole() === Userrole.Mitarbeiter) {
      this.workplaceService.selectedProductionLine$.next({
        id: this.cookieService.get("productionLine").split("%")[0],
        name: this.cookieService.get("productionLine").split("%")[1],
      });
      this.workplaceService.selectedWorkplace$.next({
        id: this.cookieService.get("workplace").split("%")[0],
        name: this.cookieService.get("workplace").split("%")[1],
      })
    }
    this.fetchChartData();
  }

  onSearchTermChange(searchTerm: string) {
    if (searchTerm.trim() === "") return;
    console.log('searchTerm', searchTerm);
    this.showFilterChartSection.set(true)
    this.searchService.setSearchTerm(searchTerm);
  }

  onFilterValueChange(filterParams: FilterParams) {
    console.log('trigger report fetching', filterParams);
    this.filterParams.set(filterParams);
    this.searchService.setFilterValues(filterParams);
    this.fetchChartData(filterParams);
  }

  private fetchChartData(filterParams: FilterParams | null = null) {
    this.apiService.getChartData({
      filterParams: filterParams || {},
      workplaceFilter: this.workplaceService.selectedWorkplaceSignal()?.id,
      productionLineFilter: this.workplaceService.selectedProductionLineSignal()?.id,
    }).subscribe((data) => {
      this.createChartData(data);
    });
  }

  selectProductionLine(line: ProductionLine): void {
    this.workplaceService.setCurrentLevel('productionLine');
    this.workplaceService.selectedProductionLine$.next({
      id: line.id!,
      name: line.name!,
    });
    this.searchService.setProductionLineFilter(line.id!)
    this.headerService.setHeaderSubTitle(line.name);
  }

  selectWorkplace(id: string) {
    this.router.navigate(["/workplace", id], {queryParams: {module: 'report'}});
  }

  get sectionHeading() {
    return this.workplaceService.selectedProductionLineSignal()?.name
      ? (this.workplaceService.selectedWorkplaceSignal()?.name
        ? this.workplaceService.selectedWorkplaceSignal()?.name
        : this.workplaceService.selectedProductionLineSignal()?.name)
      : "Gesamtübersicht"
  }

  setPage(event: { page: number, tableType: TableType }) {
    const {page, tableType} = event
    this.searchService.setPage(tableType, page);
  }

  createChartData(data: StandstillStatistics) {
    const TOTAL_HOURS = this.millisecondsToHours(data.total_interval_duration_ms);
    /*const standstillCreatedAndResolvedWithin = this.millisecondsToHours(data.total_resolved_within_interval_ms);
    const standstillCreatedBeforeSolvedWithin = this.millisecondsToHours(data.created_before_solved_within_ms);
    const standstillCreatedWithinUnresolved = this.millisecondsToHours(data.created_within_unresolved_ms);
    const standstillCreatedBeforeUnresolved = this.millisecondsToHours(data.created_before_unresolved_ms);*/
    const totalStandstillTime = this.millisecondsToHours(data.total_resolution_time_ms);
    const chartData = {
      labels: ['Stillstand', 'Betrieb'],
      datasets: [
        {
          label: 'Zeit in h',
          data: [totalStandstillTime > TOTAL_HOURS ? TOTAL_HOURS : totalStandstillTime, Math.max(TOTAL_HOURS - totalStandstillTime, 0)],
          backgroundColor: [this.getQundisColor('chart-standstill'), this.getQundisColor('chart-idle')],
          hoverOffset: 2
        }
      ]
    };
    this.chartData.set(chartData);
  }

  millisecondsToHours(milliseconds: number) {
    return milliseconds / 1000 / 60 / 60;
  }

  getQundisColor(colorName: string) {
    return getComputedStyle(document.documentElement).getPropertyValue(`--qundis-${colorName}`).trim();
  }

  getFilterTimeInterval() {
    if (this.filterParams()?.startDate && this.filterParams()?.endDate) {
      // startdate and enddate are set - return startdate - enddate
      return `${formatDate(this.filterParams()?.startDate!, 'dd.MM.YYYY', 'de-de')} -
      ${formatDate(this.filterParams()?.endDate!, 'dd.MM.YYYY', 'de-de')}`;
    } else if (this.filterParams()?.startDate && !this.filterParams()?.endDate) {
      // only startdate is set - return startdate - current date
      return `${formatDate(this.filterParams()?.startDate!, 'dd.MM.YYYY', 'de-de')} -
      ${formatDate(new Date(), 'dd.MM.YYYY', 'de-de')}`
    } else if (!this.filterParams()?.startDate && this.filterParams()?.endDate) {
      // only enddate is set - return till enddate
      return `bis zum ${formatDate(this.filterParams()?.endDate!, 'dd.MM.YYYY', 'de-de')}`
    } else {
      // no date is set - return till current date
      return `bis zum ${formatDate(new Date(), 'dd.MM.YYYY', 'de-de')}`
    }
  }

  back() {
    this.showFilterChartSection.set(false);
    if (this.workplaceService.currentLevelSignal() === 'productionLine' && this.workplaceService.hasAccessToLevel('all')) {
      this.workplaceService.setCurrentLevel('all');
      this.workplaceService.selectedProductionLine$.next(null);
      this.searchService.setProductionLineFilter(null)
      this.headerService.setHeaderSubTitle(
        this.workplaceService.selectedProductionLineSignal()?.name ||
        "Gesamtübersicht"
      );
    } else {
      this.router.navigate(["/dashboard"]);
    }
  }
}
